import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function BramptonServiceAreaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-shield-check-line',
      title: 'Alarm Systems Brampton',
      description: 'Professional alarm system installation and monitoring for Brampton homes and businesses. 24/7 protection with instant mobile alerts.',
      link: '/alarm-packages'
    },
    {
      icon: 'ri-camera-line',
      title: 'Security Cameras Brampton',
      description: 'HD and 4K security camera systems with night vision, motion detection, and cloud storage for Brampton properties.',
      link: '/camera-packages'
    },
    {
      icon: 'ri-key-2-line',
      title: 'Access Control Brampton',
      description: 'Commercial card access and keyless entry systems for Brampton businesses. Secure entry management with detailed logs.',
      link: '/access-control'
    },
    {
      icon: 'ri-live-line',
      title: 'Live Monitoring Brampton',
      description: 'Real-time video surveillance monitoring for Brampton properties. Watch your property from anywhere, anytime.',
      link: '/live-monitoring'
    }
  ];

  const neighborhoods = [
    'Bramalea', 'Heart Lake', 'Sandalwood', 'Springdale', 'Mount Pleasant',
    'Castlemore', 'Churchville', 'Bram East', 'Bram West', 'Downtown Brampton',
    'Fletcher\'s Creek', 'Northwood Park', 'Credit Valley', 'Gore Meadows', 'Vales of Castlemore'
  ];

  const stats = [
    { number: '9,200+', label: 'Brampton Installations' },
    { number: '25+', label: 'Years Serving Brampton' },
    { number: '4.9', label: 'Google Rating' },
    { number: '< 1.5hrs', label: 'Response Time' }
  ];

  const localInsights = [
    {
      icon: 'ri-line-chart-line',
      stat: '113%',
      label: 'Population Growth Since 2000',
      description: 'Brampton is one of Canada\'s fastest-growing cities, with rapid expansion creating increased demand for modern security systems in new developments and established neighborhoods alike.'
    },
    {
      icon: 'ri-home-gear-line',
      stat: '42,000+',
      label: 'New Homes Built in Last 5 Years',
      description: 'Massive residential construction in areas like Castlemore, Vales of Castlemore, and Mount Pleasant means thousands of new homeowners need professional security installation and smart home integration.'
    },
    {
      icon: 'ri-community-line',
      stat: '85%',
      label: 'Diverse Neighborhoods Require Customized Security',
      description: 'Brampton\'s diverse communities—from downtown heritage homes to suburban estates—each have unique security needs requiring tailored solutions and local expertise.'
    }
  ];

  const faqs = [
    {
      question: 'Do you serve all Brampton neighborhoods including Bramalea and Castlemore?',
      answer: 'Yes! We serve all Brampton neighborhoods including Bramalea, Heart Lake, Sandalwood, Springdale, Mount Pleasant, Castlemore, Churchville, Bram East, Bram West, Downtown Brampton, Fletcher\'s Creek, Northwood Park, Credit Valley, Gore Meadows, and Vales of Castlemore. We\'re familiar with Brampton building codes and new development requirements.'
    },
    {
      question: 'How fast is your response time in Brampton?',
      answer: 'Our average response time in Brampton is under 1.5 hours for emergency service calls. For scheduled installations and consultations, we offer same-day and next-day appointments across all Brampton neighborhoods. Our proximity to Brampton ensures faster service than national chains based outside the GTA.'
    },
    {
      question: 'Can you install security systems in new Brampton construction homes?',
      answer: 'Absolutely! We specialize in new construction security installations throughout Brampton\'s growing neighborhoods. We work with builders, developers, and homeowners to pre-wire and install complete security systems in new homes. Our systems are designed for modern smart homes with integration for cameras, alarms, door locks, and automation.'
    }
  ];

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://alliancesecuritysystems.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Brampton', item: 'https://alliancesecuritysystems.com/service-areas/brampton' }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://alliancesecuritysystems.com/service-areas/brampton#business',
      name: 'Alliance Security Systems - Brampton',
      image: 'https://public.readdy.ai/ai/img_res/8b8b771bbf9ee130510d26dbd1b9cae7.jpg',
      telephone: '+1-888-458-9181',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2355 Derry Road East, Unit 1',
        addressLocality: 'Mississauga',
        addressRegion: 'ON',
        postalCode: 'L5S 1V6',
        addressCountry: 'CA'
      },
      geo: { '@type': 'GeoCoordinates', latitude: '43.7315', longitude: '-79.7624' },
      url: 'https://alliancesecuritysystems.com/service-areas/brampton',
      priceRange: '$$',
      areaServed: {
        '@type': 'City',
        name: 'Brampton',
        containedInPlace: { '@type': 'AdministrativeArea', name: 'Ontario' }
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Brampton Security Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Alarm Systems Brampton', description: 'Professional alarm system installation and 24/7 monitoring for Brampton homes and businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security Cameras Brampton', description: 'HD and 4K security camera installation with remote viewing for Brampton properties.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Access Control Brampton', description: 'Commercial card access and keyless entry systems for Brampton businesses.' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'New Construction Security Brampton', description: 'Pre-wiring and security system installation for new Brampton homes.' } }
        ]
      },
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '480', bestRating: '5' }
    }
  ];

  return (
    <>
      <SEO
        title="Security Systems Brampton | Alarm & Camera Installation | New Construction Experts"
        description="Brampton's trusted security company since 1999. Professional alarm systems, security cameras, and access control. 9,200+ Brampton installations. New construction specialists."
        keywords="security systems Brampton, alarm systems Brampton, security cameras Brampton, access control Brampton, Bramalea security, Castlemore security, new construction security Brampton"
        canonical="/service-areas/brampton"
        structuredData={structuredData}
        faqItems={faqs}
      />

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                Home
              </Link>
              <Link to="/service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>
                Service Areas
              </Link>
              <a href="tel:18884589181" className="bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all whitespace-nowrap cursor-pointer">
                1-888-458-9181
              </a>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Home</Link>
              <Link to="/service-areas" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>Service Areas</Link>
              <a href="tel:18884589181" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">1-888-458-9181</a>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-[50vh] sm:min-h-[70vh] flex items-center pt-16 sm:pt-20">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Brampton%20Ontario%20cityscape%20with%20modern%20residential%20neighborhoods%20and%20green%20spaces%20aerial%20view%20professional%20photography%20warm%20natural%20lighting%20suburban%20development&width=1920&height=1080&seq=brampton1&orientation=landscape" 
              alt="Brampton Cityscape"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <i className="ri-map-pin-2-fill text-[#D4A853] text-lg sm:text-xl"></i>
                <span className="text-[#D4A853] font-semibold text-sm sm:text-base">Brampton, Ontario</span>
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                <strong>Security Systems Brampton</strong> - Protecting Canada's Fastest-Growing City
              </h1>
              <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">
                As one of Canada's fastest-growing cities, Brampton faces unique security challenges with rapid development and diverse neighborhoods. With over 9,200 installations across Bramalea, Castlemore, Heart Lake, and beyond, we specialize in both new construction security and upgrading existing homes. Our expertise in Brampton's booming real estate market makes us the go-to choice for modern security solutions.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                <Link 
                  to="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Quote in Brampton
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </Link>
                <a 
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav className="bg-white border-b border-gray-100 py-3" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ol className="flex items-center gap-2 text-xs text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/service-areas" className="hover:text-[#D4A853] transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">Service Areas</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li className="flex items-center"><i className="ri-arrow-right-s-line text-gray-400"></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-[#D4A853] font-medium" itemProp="name">Brampton</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>

        {/* Stats Section */}
        <section className="py-8 sm:py-12 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-4xl font-bold text-[#D4A853] mb-1 sm:mb-2">{stat.number}</p>
                  <p className="text-white/70 text-xs sm:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Security Insights */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                BRAMPTON SECURITY INSIGHTS
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Why Brampton Properties Need Professional Security
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Brampton-specific security challenges and solutions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {localInsights.map((insight, index) => (
                <div key={index} className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-gray-100 hover:shadow-xl transition-all">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#D4A853]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${insight.icon} text-2xl sm:text-3xl text-[#D4A853]`}></i>
                  </div>
                  <p className="text-3xl sm:text-4xl font-bold text-[#D4A853] mb-2">{insight.stat}</p>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-3">{insight.label}</h3>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                BRAMPTON SECURITY SERVICES
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Professional Security Solutions in Brampton
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive security services tailored for Brampton homes and businesses
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p>
                  <Link to={service.link} className="text-[#D4A853] font-semibold flex items-center gap-2 hover:gap-3 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                BRAMPTON FAQ
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Frequently Asked Questions About Brampton Security
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="text-base sm:text-lg font-semibold text-[#0A1628] pr-4">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl text-[#D4A853] flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Blog Posts */}
        <RelatedBlogPosts city="brampton" />

        {/* Brampton Security Resource Centre */}
        <section className="py-16 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                BRAMPTON RESOURCE CENTRE
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Complete Security Guides for Brampton
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                In-depth local guides covering Brampton residential and commercial security — from the 410/427 cargo corridor to the Bramalea industrial zone
              </p>
            </div>

            {/* Two residential guides side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {[
                {
                  slug: 'best-home-security-company-brampton',
                  title: 'Best Home Security Company in Brampton: The Definitive 2026 Ranking & Buyers Guide',
                  excerpt: 'ULC-certified monitoring, Peel Regional Police response protocols, Brampton\'s highest break-in neighbourhoods, and why Alliance Security Systems is ranked #1 across Peel Region.',
                  category: 'Security Tips',
                  readTime: '17 min read',
                  image: 'https://readdy.ai/api/search-image?query=modern Brampton Ontario suburban residential street at golden hour large detached brick home with professional security camera mounted above front door warm porch lighting manicured front lawn mature tree-lined street safe neighbourhood Peel Region Canada clear evening sky&width=600&height=380&seq=blog25&orientation=landscape',
                },
                {
                  slug: 'best-alarm-system-company-brampton',
                  title: 'Best Alarm System Company in Brampton: The Complete 2025 Guide',
                  excerpt: 'Everything Brampton homeowners need — ULC-certified monitoring, Peel Regional Police dispatch, neighbourhood risk profiles, and transparent pricing for 2025.',
                  category: 'Security Tips',
                  readTime: '14 min read',
                  image: 'https://readdy.ai/api/search-image?query=modern Brampton suburban neighbourhood at golden hour with large detached home featuring professional alarm system panel glowing near front entrance manicured lawn mature trees Ontario Canada residential street warm evening light&width=600&height=380&seq=blog17&orientation=landscape',
                },
              ].map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full uppercase tracking-wide">{post.category}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors line-clamp-2">{post.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 flex items-center gap-1"><i className="ri-time-line"></i>{post.readTime}</span>
                      <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Read Guide <i className="ri-arrow-right-line"></i></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Commercial guide — wide landscape card */}
            <Link
              to="/blog/commercial-security-company-brampton"
              className="group block bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
            >
              <div className="flex flex-col md:flex-row">
                <div className="relative md:w-80 h-48 md:h-auto flex-shrink-0 overflow-hidden bg-gray-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Brampton Ontario commercial and industrial district at dusk Highway 410 corridor professional business park buildings security cameras mounted on exterior walls modern logistics warehousing facilities Bramalea industrial zone clear evening sky Peel Region Canada urban skyline&width=640&height=400&seq=blog32&orientation=landscape"
                    alt="Best Commercial Security Company Brampton"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block px-3 py-1 bg-[#D4A853] text-white text-xs font-semibold rounded-full uppercase tracking-wide">Commercial Security</span>
                  </div>
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-building-2-line text-[#D4A853] text-lg"></i>
                    <span className="text-xs font-semibold text-[#D4A853] uppercase tracking-wide">Brampton Business Guide 2026</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors">
                    Best Commercial Security Company in Brampton: The Definitive 2026 Guide
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    Highway 410/427 cargo corridor, Bramalea industrial zone after-hours break-ins, Heart Lake Business Park pharmaceutical security, and organized retail crime at Bramalea City Centre — the complete commercial security guide for Brampton businesses.
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>17 min read</span>
                      <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>Brampton, ON</span>
                    </div>
                    <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">Read Full Guide <i className="ri-arrow-right-line"></i></span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Why a Local Expert Matters */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/20 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">LOCAL ADVANTAGE</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Why a Local Security Expert Matters in Brampton</h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">National chains can't match the speed, knowledge, and accountability of a true local provider</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: 'Under 1.5-Hour Response',
                  description: 'Our Mississauga-based technicians are minutes from Brampton — not dispatched from a distant call centre. Brampton emergencies get fast, local responses.',
                },
                {
                  icon: 'ri-government-line',
                  title: 'Brampton Bylaw Knowledge',
                  description: 'We know Peel Region\'s alarm permit requirements, false alarm bylaws, and City of Brampton regulations — so your system stays fully compliant.',
                },
                {
                  icon: 'ri-user-star-line',
                  title: 'Local References',
                  description: '"Alliance installed our new construction home in Castlemore — they pre-wired everything perfectly and the smart integration works flawlessly." — Brampton homeowner',
                },
                {
                  icon: 'ri-percent-line',
                  title: 'Ontario Insurance Discounts',
                  description: 'Monitored security systems qualify for up to 20% home insurance discounts in Ontario. Our ULC-certified monitoring satisfies all major insurers including Intact and Aviva.',
                },
              ].map((point, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 sm:p-8 hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-[#D4A853]/20 rounded-full flex items-center justify-center mb-4">
                    <i className={`${point.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{point.title}</h3>
                  <p className="text-white/65 text-xs sm:text-sm leading-relaxed">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  WHY BRAMPTON TRUSTS US
                </span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">
                  Brampton's Most Trusted Security Company
                </h2>
                <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
                  For over 25 years, we've been protecting Brampton homes and businesses with state-of-the-art security solutions.
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  {[
                    'Licensed and insured Brampton security professionals',
                    'New construction security specialists',
                    'Same-day service available across Brampton',
                    'Factory-certified technicians for all major brands',
                    '24/7 local monitoring and support',
                    'Free on-site security assessments'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative w-full h-[300px] sm:h-[500px] rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20installing%20camera%20system%20in%20new%20modern%20Brampton%20home%20construction%20clean%20workspace%20expert%20installation&width=800&height=600&seq=brampton2&orientation=landscape" 
                  alt="Security Installation Brampton"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Section */}
        <section className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                SERVICE COVERAGE
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Brampton Neighborhoods We Serve
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Providing security services across all Brampton neighborhoods
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 text-center hover:bg-[#D4A853]/10 hover:border-[#D4A853]/30 border border-gray-100 transition-all duration-300"
                >
                  <span className="text-[#0A1628] font-medium text-xs sm:text-sm">{neighborhood}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Office & Map */}
        <section className="py-12 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">OUR OFFICE & SERVICE AREA</span>
                <h2 className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">Serving Brampton from Our Mississauga Office</h2>
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {[
                    { icon: 'ri-map-pin-line', label: 'Address', content: '2355 Derry Road East, Unit 1\nMississauga, ON L5S 1V6' },
                    { icon: 'ri-phone-line', label: 'Phone', content: '1-888-458-9181', isPhone: true },
                    { icon: 'ri-time-line', label: 'Hours', content: 'Mon–Fri: 8:00 AM – 6:00 PM\nSat: 9:00 AM – 2:00 PM\nSun: Closed' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#D4A853] text-lg sm:text-xl`}></i>
                      </div>
                      <div>
                        <p className="font-semibold text-[#0A1628] mb-1 text-sm sm:text-base">{item.label}</p>
                        {item.isPhone ? (
                          <a href="tel:18884589181" className="text-[#D4A853] hover:text-[#B8923D] font-semibold text-sm sm:text-base cursor-pointer">{item.content}</a>
                        ) : (
                          <p className="text-gray-600 text-xs sm:text-sm whitespace-pre-line">{item.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">
                  Schedule Consultation <i className="ri-calendar-line"></i>
                </Link>
              </div>
              <div className="relative w-full h-[300px] sm:h-[450px] rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92138.12!2d-79.7624!3d43.7315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b15eaa5d05abf%3A0x352d5a00f82c9f4!2sBrampton%2C%20ON!5e0!3m2!1sen!2sca!4v1699999999999!5m2!1sen!2sca"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" title="Brampton Service Area Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-24 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Secure Your Brampton Property?
            </h2>
            <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">
              Get a free security assessment from Brampton's most trusted security experts
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
              <Link 
                to="/#contact"
                className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Schedule Free Consultation
              </Link>
              <a 
                href="tel:18884589181"
                className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>

        {/* Commercial Security Cross-Link Banner */}
        <section className="py-12 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <i className="ri-building-2-line text-white text-2xl"></i>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Looking for Commercial Security in Brampton?</h3>
                </div>
                <p className="text-white/90 text-sm sm:text-base">
                  Explore our dedicated commercial security solutions for Brampton businesses — cameras, alarms, access control & monitoring.
                </p>
              </div>
              <Link
                to="/commercial-security/brampton"
                className="bg-white text-[#D4A853] px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center gap-2"
              >
                View Commercial Security Brampton <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] mb-6 sm:mb-8 text-center">Other GTA Service Areas</h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {[
                { name: 'Toronto', slug: 'toronto' },
                { name: 'Mississauga', slug: 'mississauga' },
                { name: 'Oakville', slug: 'oakville' },
                { name: 'Hamilton', slug: 'hamilton' },
                { name: 'Richmond Hill', slug: 'richmond-hill' },
                { name: 'Vaughan', slug: 'vaughan' },
                { name: 'Whitby', slug: 'whitby' },
                { name: 'Ajax', slug: 'ajax' },
                { name: 'Oshawa', slug: 'oshawa' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/service-areas/${city.slug}`}
                  className="bg-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-medium text-gray-700 hover:text-[#D4A853] hover:shadow-md transition-all cursor-pointer border border-gray-200"
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Brampton Security Experts Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
